import { Select, Typography } from "antd";
import { useEffect, useState } from "react";
import FiscalPeriodSelect, { ALL_TIME } from "../reports/FiscalPeriodSelect";
import {
  getCompanies,
  getCompanyDepartments,
  getCompanyFiscalPeriods,
} from "../../repository/data.repository";
import { FiscalPeriod, MAX_PAGE_SIZE } from "../../repository/data.default";
import {
  QueryCondition,
  QueryOperation,
} from "../../code/search-query/search-query";

// Resolved scope emitted to the parent on every change. `fiscalPeriods` is included so the
// parent can call `resolveRange` for its own date-window queries.
export interface AnalyticsScope {
  readonly companyUid?: string;
  readonly departmentUid?: string;
  readonly periodSel: string;
  readonly fiscalPeriods: FiscalPeriod[];
}

export interface AnalyticsScopeBarProps {
  readonly onChange: (scope: AnalyticsScope) => void;
  // Bumping this key resets the bar (e.g. when a parent overlay (re)opens).
  readonly resetKey?: unknown;
}

interface Option {
  readonly label: string;
  readonly value: string;
}

// Equality condition shorthand for the scoped reads.
const eq = (field: string, value: string): QueryCondition => ({
  field,
  operation: QueryOperation.Equal,
  values: [value],
});

// Self-contained company·department·fiscal-period scope bar. Owns its own data-fetching +
// cascade (companies on mount; departments + fiscal periods scoped to the chosen company,
// resetting the deeper scope on company change) and emits the resolved scope to the parent.
function AnalyticsScopeBar(props: AnalyticsScopeBarProps) {
  const { onChange, resetKey } = props;

  const [companies, setCompanies] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [departmentUid, setDepartmentUid] = useState<string | undefined>();
  const [periodSel, setPeriodSel] = useState<string>(ALL_TIME);

  // Load companies on mount / reset; clear scope back to its initial state.
  useEffect(() => {
    setCompanyUid(undefined);
    setDepartmentUid(undefined);
    setPeriodSel(ALL_TIME);
    getCompanies({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 200,
    })
      .then(({ data }) =>
        setCompanies(data.map((c) => ({ label: c.name, value: c.uid }))),
      )
      .catch(() => setCompanies([]));
  }, [resetKey]);

  // Cascade departments + periods on company change; reset deeper scope.
  useEffect(() => {
    setDepartmentUid(undefined);
    setDepartments([]);
    setFiscalPeriods([]);
    setPeriodSel(ALL_TIME);
    if (!companyUid) return;
    const scoped = {
      searchQuery: { conditions: [eq("companyUid", companyUid)] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    };
    getCompanyDepartments(scoped)
      .then(({ data }) =>
        setDepartments(data.map((d) => ({ label: d.name, value: d.uid }))),
      )
      .catch(() => setDepartments([]));
    getCompanyFiscalPeriods(scoped)
      .then(({ data }) => setFiscalPeriods(data))
      .catch(() => setFiscalPeriods([]));
  }, [companyUid]);

  // Emit resolved scope whenever any part changes.
  useEffect(() => {
    onChange({ companyUid, departmentUid, periodSel, fiscalPeriods });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUid, departmentUid, periodSel, fiscalPeriods]);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-1">
        <Typography.Text type="secondary" className="text-xs">
          Company
        </Typography.Text>
        <Select
          className="w-64"
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="Select a company"
          value={companyUid}
          options={companies}
          onChange={(v) => setCompanyUid(v)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Typography.Text type="secondary" className="text-xs">
          Department
        </Typography.Text>
        <Select
          className="w-56"
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="All departments"
          disabled={!companyUid}
          value={departmentUid}
          options={departments}
          onChange={(v) => setDepartmentUid(v)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Typography.Text type="secondary" className="text-xs">
          Fiscal period
        </Typography.Text>
        <FiscalPeriodSelect
          periods={fiscalPeriods}
          value={periodSel}
          onChange={(v) => setPeriodSel(v)}
          disabled={!companyUid}
          className="w-56"
        />
      </div>
    </div>
  );
}

export default AnalyticsScopeBar;
